import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { webUrlsQueryOptions } from '../api/webUrls.api';
import { useWebUrlActionModals } from '../hooks/useWebUrlActionModals';
import type { WebUrl } from '../types/webUrl.types';
import { getWebUrlColumns } from './WebUrlColumns';
import { WebUrlDeleteConfirmation } from './WebUrlDeleteConfirmation';
import { WebUrlFormModal } from './WebUrlFormModal';

const route = getRouteApi('/_auth/settings/web-urls');

const toSearchableValues = (webUrl: WebUrl) => [
  webUrl.title,
  webUrl.description,
  webUrl.actionLink,
];

export function WebUrlListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const {
    modalState,
    webUrlToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useWebUrlActionModals();

  const { data, isLoading, error, refetch } = useQuery(webUrlsQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getWebUrlColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Web URLs"
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Search by title or link',
          }}
          createAction={{ label: 'Add Web URL', onClick: openCreateModal }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load web URLs"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="title"
            className="lg:flex-1"
          />
        )}
      </div>

      <WebUrlFormModal
        webUrl={modalState?.webUrl ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <WebUrlDeleteConfirmation
        webUrl={webUrlToDelete}
        open={webUrlToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </>
  );
}
